import { AuthProvider, useAuth } from 'react-oidc-context';
import { config } from '@/config/app';
import { AppHeader } from '@/layout/AppHeader';
import '@/styles/global.css';
import { useEffect } from 'react';

const oidcConfig = {
  authority: config.authority,
  // biome-ignore lint/style/useNamingConvention: OIDC spec requires snake_case property names
  client_id: config.clientId,
  // biome-ignore lint/style/useNamingConvention: OIDC spec requires snake_case property names
  redirect_uri: window.location.origin,
};

/**
 * Main content shown after successful authentication.
 * Displays user profile and roles from the OIDC session.
 */
const AuthenticatedHome = (): React.ReactElement => {
  const auth = useAuth();
  const userProfile = auth.user?.profile;

  const userName: string =
    (userProfile?.name as string | undefined) ??
    (userProfile?.preferred_username as string | undefined) ??
    (userProfile?.email as string | undefined) ??
    'User';

  const userEmail = userProfile?.email as string | undefined;
  const realmRoles = (userProfile?.realm_access as { roles?: string[] } | undefined)?.roles ?? [];

  return (
    <div className="mx-auto max-w-xl p-8">
      <div className="card p-6">
        <h1 className="mb-4 text-2xl font-bold text-slate-800">Welcome, {userName}</h1>
        <dl className="space-y-3 text-sm">
          {userEmail && (
            <div>
              <dt className="font-semibold text-slate-500">Email</dt>
              <dd className="text-slate-800">{userEmail}</dd>
            </div>
          )}
          {userProfile?.preferred_username && (
            <div>
              <dt className="font-semibold text-slate-500">Username</dt>
              <dd className="text-slate-800">{userProfile.preferred_username as string}</dd>
            </div>
          )}
          {userProfile?.sub && (
            <div>
              <dt className="font-semibold text-slate-500">Subject (sub)</dt>
              <dd className="break-all font-mono text-xs text-slate-600">{userProfile.sub}</dd>
            </div>
          )}
          {realmRoles.length > 0 && (
            <div>
              <dt className="font-semibold text-slate-500">Realm roles</dt>
              <dd className="mt-1 flex flex-wrap gap-1">
                {realmRoles.map((role) => (
                  <span key={role} className="rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                    {role}
                  </span>
                ))}
              </dd>
            </div>
          )}
        </dl>
      </div>
    </div>
  );
};

/**
 * Inner app that requires authentication.
 * Triggers login redirect when unauthenticated.
 */
const AuthenticatedApp = (): React.ReactElement => {
  const auth = useAuth();

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !auth.error) {
      auth.signinRedirect({
        // biome-ignore lint/style/useNamingConvention: OIDC spec requires snake_case property names
        redirect_uri: window.location.href,
      });
    }
  }, [auth]);

  if (auth.isLoading) {
    return <div className="flex h-screen items-center justify-center text-slate-500">Loading…</div>;
  }

  if (auth.error) {
    return (
      <div className="flex h-screen items-center justify-center text-red-600">
        Authentication error: {auth.error.message}
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return <div className="flex h-screen items-center justify-center text-slate-500">Redirecting to login…</div>;
  }

  const handleLogout = (): void => {
    auth.signoutRedirect();
  };

  return (
    <>
      <AppHeader onLogout={handleLogout} />
      <main>
        <AuthenticatedHome />
      </main>
    </>
  );
};

/**
 * Root application component.
 * Wraps the app in OIDC provider.
 */
const App = (): React.ReactElement => (
  <AuthProvider {...oidcConfig}>
    <AuthenticatedApp />
  </AuthProvider>
);

export default App;
